import React from 'react';
import { Box, Paper, Typography, Stack, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Workspace kit — the shared "full-width transaction workspace" design system
// used by the redesigned create/edit forms (Journal Entry, Sales Order, and
// the Procurement forms). The palette is deliberately a LOCAL light palette
// (not theme tokens) so the workspace keeps its clean reference look across
// tenants. Import these primitives instead of copy-pasting them per page.
// ---------------------------------------------------------------------------

export const P = {
  bg: '#f5f7fb', card: '#ffffff', line: '#eceff4', tline: '#f1f5f9', border: '#e2e8f0',
  primary: '#2563eb', primarySoft: '#eff4ff', primaryBorder: '#cddaf6',
  strong: '#0f172a', text: '#334155', muted: '#64748b', label: '#6b7280', ph: '#94a3b8',
  green: '#16a34a', greenBg: '#dcfce7', amber: '#d97706', amberBg: '#fef3c7', red: '#dc2626', redBg: '#fef2f2',
  cyan: '#0891b2', purple: '#7c3aed', head: '#f8fafc', draftBg: '#dbeafe',
} as const;

export const cardSx = { bgcolor: P.card, border: `1px solid ${P.line}`, borderRadius: '14px', boxShadow: '0 1px 2px rgba(16,24,40,.05)' } as const;
export const labelSx = { fontSize: '11px', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase' as const, color: P.label, display: 'block', mb: '7px' };
export const fieldSx = { '& .MuiOutlinedInput-root': { minHeight: 42, borderRadius: '10px', bgcolor: '#fff', fontSize: '13.5px', fontWeight: 500, color: P.strong, '& fieldset': { borderColor: P.border }, '&:hover fieldset': { borderColor: '#cbd5e1' }, '&.Mui-focused fieldset': { borderColor: P.primary } } } as const;
export const priBtn = { height: 40, textTransform: 'none' as const, fontWeight: 700, fontSize: '14px', bgcolor: P.primary, boxShadow: 'none', borderRadius: '9px', '&:hover': { bgcolor: '#1d4ed8', boxShadow: 'none' } };
export const outBtn = { height: 40, borderRadius: '9px', textTransform: 'none' as const, fontWeight: 600, fontSize: '14px', bgcolor: '#fff', borderColor: '#dbe3ee', color: P.primary, '&:hover': { borderColor: P.primary, bgcolor: P.primarySoft } };
export const gridInput = { '& .MuiOutlinedInput-root': { fontSize: '14px', '& fieldset': { border: 'none' }, '&:hover fieldset': { border: `1px solid ${P.border}` }, '&.Mui-focused fieldset': { border: `1px solid ${P.primary}` } } };
export const thSx = { bgcolor: P.head, borderTop: `1px solid ${P.tline}`, borderBottom: `1px solid ${P.tline}`, py: 1.25, verticalAlign: 'top' as const };
export const thTitle = { fontSize: '11px', fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase' as const, color: '#64748b', whiteSpace: 'nowrap' as const };
export const PX = { xs: 2, md: 3.25 };

export const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
  <Typography sx={labelSx}>{text}{required && <Box component="span" sx={{ color: P.red, ml: '3px' }}>*</Box>}</Typography>
);

// --- Status pill -----------------------------------------------------------
type Tone = 'draft' | 'green' | 'amber' | 'red' | 'muted' | 'primary';
const toneMap: Record<Tone, { bg: string; fg: string; bd: string }> = {
  draft: { bg: P.draftBg, fg: '#1d4ed8', bd: '#bfdbfe' },
  green: { bg: P.greenBg, fg: '#15803d', bd: '#bbf7d0' },
  amber: { bg: P.amberBg, fg: '#b45309', bd: '#fde68a' },
  red: { bg: P.redBg, fg: P.red, bd: '#fecaca' },
  muted: { bg: '#f1f5f9', fg: P.muted, bd: '#e2e8f0' },
  primary: { bg: P.primarySoft, fg: P.primary, bd: P.primaryBorder },
};

export const StatusPill: React.FC<{ label: string; tone?: Tone }> = ({ label, tone = 'draft' }) => {
  const c = toneMap[tone];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 24, px: 1.25, borderRadius: '7px', bgcolor: c.bg, color: c.fg, border: `1px solid ${c.bd}`, fontSize: '11px', fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {label}
    </Box>
  );
};

// --- Sticky workspace header ----------------------------------------------
export const WorkspaceHeader: React.FC<{
  onBack: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  chip?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ onBack, title, subtitle, chip, actions }) => (
  <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'rgba(255,255,255,.86)', backdropFilter: 'blur(8px)', borderBottom: `1px solid ${P.line}` }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: PX, py: 1.5, gap: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
        <IconButton onClick={onBack} size="small" sx={{ color: P.muted, border: `1px solid ${P.border}`, borderRadius: '9px' }}><ArrowBack sx={{ fontSize: 18 }} /></IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: '17px', fontWeight: 800, color: P.strong, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Typography>
            {chip}
          </Stack>
          {subtitle && <Typography sx={{ fontSize: '12.5px', color: P.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</Typography>}
        </Box>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>{actions}</Stack>
    </Stack>
  </Box>
);

// --- Totals + status strip (4 configurable cells) --------------------------
export interface TotalCell { label: string; value: React.ReactNode; tone?: 'strong' | 'green' | 'red' | 'amber' | 'primary'; sub?: React.ReactNode; }
const cellColor: Record<NonNullable<TotalCell['tone']>, string> = { strong: P.strong, green: P.green, red: P.red, amber: P.amber, primary: P.primary };

export const TotalsStrip: React.FC<{ cells: TotalCell[] }> = ({ cells }) => (
  <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${cells.length}, minmax(0,1fr))` } }}>
      {cells.map((c, i) => (
        <Box key={c.label} sx={{ p: { xs: 1.75, md: 2.25 }, borderRight: { md: i < cells.length - 1 ? `1px solid ${P.line}` : 'none' }, borderBottom: { xs: i < cells.length - 2 ? `1px solid ${P.line}` : 'none', md: 'none' } }}>
          <Typography sx={{ ...labelSx, mb: '6px' }}>{c.label}</Typography>
          <Typography sx={{ fontSize: '20px', fontWeight: 800, color: cellColor[c.tone || 'strong'], lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.value}</Typography>
          {c.sub && <Typography sx={{ fontSize: '12px', color: P.muted, mt: '2px' }}>{c.sub}</Typography>}
        </Box>
      ))}
    </Box>
  </Paper>
);

// --- Validation strip ------------------------------------------------------
export interface ValidationItem { ok: boolean; label: string; }

export const ValidationStrip: React.FC<{ items: ValidationItem[] }> = ({ items }) => (
  <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25} sx={{ px: PX, py: 1.25 }}>
    {items.map((it) => (
      <Stack key={it.label} direction="row" alignItems="center" spacing={0.75} sx={{ px: 1.25, py: '5px', borderRadius: '8px', bgcolor: it.ok ? P.greenBg : P.redBg, border: `1px solid ${it.ok ? '#bbf7d0' : '#fecaca'}` }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: it.ok ? P.green : P.red }} />
        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: it.ok ? '#15803d' : P.red }}>{it.label}</Typography>
      </Stack>
    ))}
  </Stack>
);
